(() => {
  if (customElements.get('custom-product-page')) return;

  const DIMENSIONS = /(\d+(?:\.\d+)?)\s*['’′]?\s*[x×X]\s*(\d+(?:\.\d+)?)/;
  // Area in square feet of a size such as 4'x6', 6'2"x9'7" or 4x6 (inches count as twelfths of a foot).
  // Sizes shown in centimetres (120x180 cm) are converted with cmPerFoot.
  const FEET_INCHES = /(\d+(?:\.\d+)?)\s*['’′]\s*(?:(\d+(?:\.\d+)?)\s*["”″])?\s*[x×X]\s*(\d+(?:\.\d+)?)\s*['’′]?\s*(?:(\d+(?:\.\d+)?)\s*["”″])?/;
  const sizeArea = (value, cmPerFoot = 30) => {
    const centimetres = /cm/i.test(String(value || ''));
    const feet = centimetres ? null : String(value || '').match(FEET_INCHES);
    if (feet) {
      const width = parseFloat(feet[1]) + (parseFloat(feet[2]) || 0) / 12;
      const length = parseFloat(feet[3]) + (parseFloat(feet[4]) || 0) / 12;
      return width * length;
    }
    const plain = String(value || '').match(DIMENSIONS);
    if (!plain) return 0;
    const scale = centimetres ? cmPerFoot * cmPerFoot : 1;
    return (parseFloat(plain[1]) * parseFloat(plain[2])) / scale;
  };

  // Shopify money format ("${{amount}}", "{{amount_with_comma_separator}} €", ...), dropping ".00".
  function formatMoney(cents, format) {
    const value = (cents || 0) / 100;
    const group = (num, decimals, thousands, decimal) => {
      const [whole, fraction] = num.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction && Number(fraction) !== 0 ? grouped + decimal + fraction : grouped;
    };
    const match = (format || '${{amount}}').match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    const amount =
      {
        amount: group(value, 2, ',', '.'),
        amount_no_decimals: group(value, 0, ',', '.'),
        amount_with_comma_separator: group(value, 2, '.', ','),
        amount_no_decimals_with_comma_separator: group(value, 0, '.', ','),
        amount_with_apostrophe_separator: group(value, 2, "'", '.'),
      }[key] ?? group(value, 2, ',', '.');
    return (format || '${{amount}}').replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  const formatSize = (value) => value.replace(/[’']/g, '′').replace(/′\s*[xX]\s*/, '′ × ');

  customElements.define(
    'custom-product-page',
    class CustomProductPage extends HTMLElement {
      connectedCallback() {
        try {
          this.product = JSON.parse(this.querySelector('[data-cpr-product]').textContent);
        } catch (error) {
          return;
        }
        this.form = document.getElementById(this.dataset.formId);
        this.moneyFormat = this.dataset.moneyFormat;
        this.roundPrices = this.dataset.roundPrices === 'true';
        this.customValue = (this.dataset.customValue || '').trim();
        this.sizePosition = parseInt(this.dataset.sizePosition || '0', 10);
        // Custom size sold through a hidden unit-priced product (the product has no Custom size variant).
        this.customVariantId = this.dataset.customVariantId || '';
        this.customUnitPrice = parseInt(this.dataset.customUnitPrice || '0', 10);
        // Optional server that creates one variant per custom size (Theme settings > Sizes and units).
        this.customEndpoint = (this.dataset.customEndpoint || '').trim().replace(/\/+$/, '');
        this.slides = Array.from(this.querySelectorAll('[data-cpr-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-cpr-thumb]'));
        this.index = Math.max(
          0,
          this.slides.findIndex((slide) => !slide.hidden),
        );
        this.mobile = window.matchMedia('(max-width: 759px)');

        this.bindGallery();
        this.bindOptions();
        this.bindCustomSize();
        this.bindForm();
        this.bindGuide();
        this.bindAccordions();
        this.bindSave();
        this.bindSticky();
        this.renderDelivery();
        this.updateProgress();
      }

      /* ---------- Gallery ---------- */
      bindGallery() {
        this.track = this.querySelector('[data-cpr-track]');
        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () =>
            this.showMedia(this.slides.findIndex((s) => s.dataset.mediaId === thumb.dataset.mediaId)),
          ),
        );
        this.querySelector('[data-cpr-prev]')?.addEventListener('click', () => this.showMedia(this.index - 1));
        this.querySelector('[data-cpr-next]')?.addEventListener('click', () => this.showMedia(this.index + 1));
        this.track?.addEventListener(
          'scroll',
          () => {
            if (!this.mobile.matches) return;
            const index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
            if (index !== this.index) {
              this.index = index;
              this.syncThumbs();
              this.updateProgress();
            }
          },
          { passive: true },
        );

        this.lightbox = this.querySelector('[data-cpr-lightbox]');
        if (this.lightbox) {
          this.querySelectorAll('[data-cpr-slide][data-media-type="image"] img').forEach((img) =>
            img.addEventListener('click', () => {
              if (!this.mobile.matches) this.openLightbox(this.slides.indexOf(img.closest('[data-cpr-slide]')));
            }),
          );
          this.lightbox
            .querySelector('[data-cpr-lightbox-close]')
            ?.addEventListener('click', () => this.closeLightbox());
          this.lightbox
            .querySelector('[data-cpr-lightbox-prev]')
            ?.addEventListener('click', () => this.stepLightbox(-1));
          this.lightbox
            .querySelector('[data-cpr-lightbox-next]')
            ?.addEventListener('click', () => this.stepLightbox(1));
          this.lightbox.addEventListener('click', (event) => {
            if (event.target === this.lightbox || event.target.classList.contains('cpr-lightbox__frame'))
              this.closeLightbox();
          });
          this.lightbox.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') this.closeLightbox();
            if (event.key === 'ArrowLeft') this.stepLightbox(-1);
            if (event.key === 'ArrowRight') this.stepLightbox(1);
          });
        }
      }

      showMedia(index) {
        const count = this.slides.length;
        if (!count) return;
        this.index = ((index % count) + count) % count;
        if (this.mobile.matches && this.track) {
          this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: 'smooth' });
        } else {
          this.slides.forEach((slide, i) => (slide.hidden = i !== this.index));
        }
        this.slides.forEach((slide, i) => {
          if (i !== this.index) slide.querySelectorAll('video').forEach((video) => video.pause());
        });
        this.syncThumbs();
        this.updateProgress();
      }

      showMediaById(id) {
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
        if (index >= 0) this.showMedia(index);
      }

      syncThumbs() {
        const id = this.slides[this.index]?.dataset.mediaId;
        this.thumbs.forEach((thumb) => {
          const active = thumb.dataset.mediaId === id;
          thumb.classList.toggle('is-active', active);
          if (active) {
            thumb.setAttribute('aria-current', 'true');
            const list = thumb.parentElement;
            if (
              thumb.offsetTop < list.scrollTop ||
              thumb.offsetTop + thumb.offsetHeight > list.scrollTop + list.clientHeight
            ) {
              list.scrollTo({ top: thumb.offsetTop - 22, behavior: 'smooth' });
            }
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
      }

      updateProgress() {
        const count = this.slides.length;
        const fill = this.querySelector('[data-cpr-fill]');
        const current = this.querySelector('[data-cpr-current]');
        if (fill) fill.style.width = `${((this.index + 1) / Math.max(1, count)) * 100}%`;
        if (current) current.textContent = String(this.index + 1).padStart(2, '0');
      }

      imageSources() {
        return this.slides.map((slide) => {
          const img = slide.querySelector('img');
          return img ? { src: img.currentSrc || img.src, alt: img.alt } : null;
        });
      }

      openLightbox(index) {
        this.lightboxItems = this.imageSources();
        this.lightboxIndex = index;
        this.lastFocus = document.activeElement;
        this.lightbox.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        this.renderLightbox();
        this.lightbox.querySelector('[data-cpr-lightbox-close]')?.focus();
      }

      stepLightbox(delta) {
        const items = this.lightboxItems || [];
        if (!items.length) return;
        let next = this.lightboxIndex;
        for (let i = 0; i < items.length; i++) {
          next = (((next + delta) % items.length) + items.length) % items.length;
          if (items[next]) break;
        }
        this.lightboxIndex = next;
        this.renderLightbox();
      }

      renderLightbox() {
        const item = this.lightboxItems[this.lightboxIndex];
        const img = this.lightbox.querySelector('[data-cpr-lightbox-image]');
        if (!item) return;
        img.src = item.src.replace(/([?&]width=)\d+/, '$12400');
        img.alt = item.alt;
        const counter = this.lightbox.querySelector('[data-cpr-lightbox-counter]');
        if (counter) counter.textContent = `${this.lightboxIndex + 1} / ${this.lightboxItems.length}`;
      }

      closeLightbox() {
        if (!this.lightbox || this.lightbox.hidden) return;
        this.lightbox.hidden = true;
        document.documentElement.style.overflow = '';
        this.lastFocus?.focus();
      }

      // "Show prices as whole numbers" (theme setting): 700.05 → 700, 700.50 → 701.
      money(cents) {
        return formatMoney(this.roundPrices ? Math.round((cents || 0) / 100) * 100 : cents, this.moneyFormat);
      }

      /* ---------- Variants ---------- */
      bindOptions() {
        this.optionInputs = Array.from(
          document.querySelectorAll(`[form="${this.dataset.formId}"][data-cpr-option-input]`),
        );
        this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onVariantChange()));
        this.querySelectorAll('[data-cpr-choice]').forEach((choice) => {
          choice.addEventListener('change', (event) => {
            if (!event.target.matches('input[type="radio"]')) return;
            const label = choice.querySelector('[data-cpr-choice-label]');
            if (label) label.textContent = event.target.value;
            const field = choice.querySelector('[data-cpr-choice-value]');
            if (field) field.value = event.target.value;
          });
        });
        this.variant = this.currentVariant();
      }

      selectedOptions() {
        const selected = [];
        this.optionInputs.forEach((input) => {
          if (input.checked) selected[parseInt(input.dataset.cprOptionInput, 10) - 1] = input.value;
        });
        return selected;
      }

      currentVariant() {
        const id = this.form?.querySelector('[data-cpr-variant-id]')?.value;
        return this.product.variants.find((variant) => String(variant.id) === String(id)) || this.product.variants[0];
      }

      virtualSelected() {
        return !!this.querySelector('[data-cpr-virtual]:checked');
      }

      // A stand-in variant for the hidden custom size product, carrying the other chosen options.
      virtualVariant(selected) {
        return {
          id: this.customVariantId,
          price: this.customUnitPrice,
          compare_at_price: null,
          available: true,
          options: this.product.options.map((_, i) => selected[i]),
          featured_media: null,
          virtual: true,
        };
      }

      onVariantChange() {
        const selected = this.selectedOptions();
        if (this.customVariantId && this.virtualSelected()) {
          this.variant = this.virtualVariant(selected);
          this.updateAvailability(selected);
          const idInput = this.form?.querySelector('[data-cpr-variant-id]');
          if (idInput) idInput.value = this.customVariantId;
          this.updateSizeHint();
          this.toggleCustomSize();
          const url = new URL(window.location.href);
          url.searchParams.delete('variant');
          window.history.replaceState({}, '', url.toString());
          this.syncGuide();
          this.syncSticky();
          return;
        }
        const variant = this.product.variants.find((v) =>
          v.options.every((value, i) => selected[i] === undefined || value === selected[i]),
        );
        this.variant = variant || null;
        this.updateAvailability(selected);
        if (!variant) {
          this.setButton(false, window.variantStrings?.unavailable || 'Unavailable');
          return;
        }
        const idInput = this.form?.querySelector('[data-cpr-variant-id]');
        if (idInput) idInput.value = variant.id;
        if (variant.featured_media) this.showMediaById(variant.featured_media.id);
        this.updatePrice(variant);
        this.updateSizeHint();
        this.toggleCustomSize();
        this.setButton(variant.available);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        this.syncGuide();
        this.syncSticky();
      }

      // Mark values that have no available variant with the other current choices.
      updateAvailability(selected) {
        const sizeIndex = this.sizePosition - 1;
        this.optionInputs.forEach((input) => {
          if (input.hasAttribute('data-cpr-virtual')) return;
          const position = parseInt(input.dataset.cprOptionInput, 10) - 1;
          const candidate = selected.slice();
          candidate[position] = input.value;
          // Any size can be woven to a custom size, so the custom choice doesn't restrict the other options.
          if (this.customVariantId && candidate[sizeIndex] === this.customValue) candidate[sizeIndex] = undefined;
          const match = this.product.variants.some(
            (v) => v.available && v.options.every((value, i) => candidate[i] === undefined || value === candidate[i]),
          );
          input.closest('.cpr-value')?.classList.toggle('is-unavailable', !match);
        });
      }

      updatePrice(variant, totalCents) {
        const price = totalCents ?? variant.price;
        const compare = totalCents === undefined ? variant.compare_at_price : null;
        this.querySelectorAll('[data-cpr-price], [data-cpr-atc-price], [data-cpr-sticky-price]').forEach(
          (el) => (el.textContent = this.money(price)),
        );
        const onSale = compare && compare > price;
        this.querySelectorAll('[data-cpr-compare], [data-cpr-sticky-compare]').forEach((el) => {
          el.hidden = !onSale;
          if (onSale) {
            const hidden = el.querySelector('.cpr-visually-hidden');
            el.textContent = this.money(compare);
            if (hidden) el.prepend(hidden);
          }
        });
        this.querySelectorAll('[data-cpr-saving]').forEach((el) => {
          el.hidden = !onSale;
          if (!onSale) return;
          // Rounded to the block's "Round % off to" step (5: 39.01% → 40%).
          const step = parseFloat(el.dataset.cprSavingStep) || 1;
          const percent = Math.round(((compare - price) / compare) * 100 / step) * step;
          el.textContent = el.textContent.replace(/\d+(?:[.,]\d+)?\s*%/, `${percent}%`);
        });
      }

      updateSizeHint() {
        if (!this.sizePosition || !this.variant) return;
        const value = this.variant.options[this.sizePosition - 1];
        this.querySelectorAll('[data-cpr-size-hint]').forEach(
          (hint) => (hint.hidden = hint.dataset.cprSizeHint !== value),
        );
      }

      setButton(available, text) {
        const button = this.form?.querySelector('[data-cpr-atc]');
        if (!button) return;
        button.disabled = !available;
        const label = button.querySelector('[data-cpr-atc-text]');
        if (label) {
          if (!this.buttonLabel) this.buttonLabel = label.textContent.trim();
          label.textContent = available ? this.buttonLabel : text || window.variantStrings?.soldOut || 'Sold out';
        }
      }

      /* ---------- Custom size ---------- */
      bindCustomSize() {
        this.customBlock = this.querySelector('[data-cpr-custom-block]');
        this.custom = this.querySelector('[data-cpr-custom-size]');
        if (!this.custom) return;
        this.dims = {
          width: this.custom.querySelector('[data-cpr-dim="width"]'),
          length: this.custom.querySelector('[data-cpr-dim="length"]'),
        };
        const min = parseFloat(this.custom.dataset.min) || 1;
        const max = parseFloat(this.custom.dataset.max) || Infinity;
        const step = parseFloat(this.custom.dataset.step) || 0.5;
        // Snap to the step and keep it within min and max, without float noise (2.3000000000000003).
        const decimals = (String(step).split('.')[1] || '').length;
        const snap = (value) => Number(Math.min(max, Math.max(min, Math.round(value / step) * step)).toFixed(decimals));
        this.custom.querySelectorAll('[data-cpr-stepper]').forEach((stepper) => {
          const input = stepper.querySelector('input');
          stepper.querySelectorAll('[data-cpr-step]').forEach((button) =>
            button.addEventListener('click', () => {
              input.value = snap((parseFloat(input.value) || min) + step * parseInt(button.dataset.cprStep, 10));
              this.updateCustomSize();
            }),
          );
          input.addEventListener('input', () => this.updateCustomSize());
          input.addEventListener('change', () => {
            const value = parseFloat(input.value);
            if (!Number.isNaN(value)) input.value = snap(value);
            this.updateCustomSize();
          });
        });
        this.toggleCustomSize();
      }

      isCustom() {
        return !!(
          this.variant &&
          this.sizePosition &&
          this.customValue &&
          this.variant.options[this.sizePosition - 1] === this.customValue
        );
      }

      toggleCustomSize() {
        if (!this.custom) return;
        const on = this.isCustom();
        if (this.customBlock) this.customBlock.hidden = !on;
        Object.values(this.dims).forEach((input) => (input.disabled = !on));
        this.custom.querySelectorAll('[data-cpr-custom-prop]').forEach((input) => (input.disabled = !on));
        // A choice set to "standard sizes only" steps aside while the custom size panel has its own.
        this.querySelectorAll('[data-cpr-standard-only]').forEach((block) => (block.hidden = on));
        this.querySelectorAll('[data-cpr-standard-prop]').forEach((input) => (input.disabled = on));
        if (on) this.updateCustomSize();
        else this.setQuantity(1);
      }

      // Price scales with area from the smallest standard size, charged in units of the custom variant's price.
      updateCustomSize() {
        if (!this.isCustom()) return;
        const width = parseFloat(this.dims.width.value);
        const length = parseFloat(this.dims.length.value);
        const min = parseFloat(this.custom.dataset.min) || 1;
        const max = parseFloat(this.custom.dataset.max) || Infinity;
        const unit = this.custom.dataset.unit || 'ft';
        const error = this.custom.querySelector('[data-cpr-custom-error]');
        const tooSmall = !(width >= min && length >= min);
        const tooBig = !tooSmall && (width > max || length > max);
        const valid = !tooSmall && !tooBig;
        const sizeProp = this.custom.querySelector('[data-cpr-size-prop]');
        if (sizeProp) sizeProp.value = valid ? `${width} × ${length} ${unit}` : '';
        this.custom.querySelectorAll('.cpr-stepper').forEach((s) => s.classList.toggle('is-invalid', !valid));
        error.textContent = tooSmall ? `${min} × ${min} ${unit} minimum` : tooBig ? `${max} × ${max} ${unit} maximum` : '';
        this.custom.querySelector('[data-cpr-dim-w]').textContent = `${width || '—'} ${unit}`;
        this.custom.querySelector('[data-cpr-dim-l]').textContent = `${length || '—'} ${unit}`;
        // Drawing: width across, length down, in the real proportions (largest side 90px, smallest 18px).
        const rug = this.custom.querySelector('[data-cpr-rug]');
        if (valid && rug) {
          const scale = 90 / Math.max(width, length);
          rug.style.width = `${Math.max(18, width * scale)}px`;
          rug.style.height = `${Math.max(18, length * scale)}px`;
        }
        if (!valid) {
          this.custom.querySelector('[data-cpr-custom-total]').textContent = '—';
          this.custom.querySelector('[data-cpr-custom-area]').textContent = '—';
          this.setButton(false, this.buttonLabel);
          return;
        }
        // Price points from the standard sizes (area in sq ft → price), sorted by area.
        // Sizes of the same area are averaged.
        const cmPerFoot = parseFloat(this.custom.dataset.cmPerFoot) || 30;
        const byArea = new Map();
        this.product.variants
          .filter((v) => v.options[this.sizePosition - 1] !== this.customValue)
          .forEach((v) => {
            const sizeAreaFt = sizeArea(v.options[this.sizePosition - 1], cmPerFoot);
            if (sizeAreaFt <= 0) return;
            const key = Math.round(sizeAreaFt * 100) / 100;
            const entry = byArea.get(key) || { area: key, total: 0, count: 0 };
            entry.total += v.price;
            entry.count += 1;
            byArea.set(key, entry);
          });
        const points = [...byArea.values()]
          .map((e) => ({ area: e.area, price: e.total / e.count }))
          .sort((a, b) => a.area - b.area);
        // Prices follow square feet. Centimetre sizes are converted with the size chart's centimetres per
        // foot (30 by default: 120 × 180 cm is priced like 4' × 6'), so cm and ft sizes cost the same.
        const metric = unit === 'cm';
        const area = metric ? (width / cmPerFoot) * (length / cmPerFoot) : width * length;
        const unitPrice = this.variant.price || 1;
        // Each standard size keeps its own price; sizes in between are priced along the line between the two
        // neighbouring sizes; below the smallest or above the largest, at that size's price per sq ft.
        let total = unitPrice * area;
        if (points.length) {
          const first = points[0];
          const last = points[points.length - 1];
          if (area <= first.area) total = (first.price / first.area) * area;
          else if (area >= last.area) total = (last.price / last.area) * area;
          else {
            const upper = points.findIndex((pt) => pt.area >= area);
            const below = points[upper - 1];
            const above = points[upper];
            total = below.price + ((above.price - below.price) * (area - below.area)) / (above.area - below.area);
          }
        }
        const quantity = Math.max(1, Math.ceil(total / unitPrice));
        this.setQuantity(quantity);
        // With the custom size server the size is sold as one variant priced in whole currency units;
        // the quantity above stays as the fallback if the server can't be reached.
        const cents = this.usesCustomEndpoint() ? Math.max(100, Math.ceil(total / 100) * 100) : quantity * unitPrice;
        this.customSize = { width, length, unit };
        // Prepare the size in the background once the shopper stops changing it: Shopify needs a few seconds
        // before a new variant can be added to the cart.
        if (this.usesCustomEndpoint()) {
          clearTimeout(this.prepareTimer);
          this.prepareTimer = setTimeout(() => this.customSizeVariant(), 800);
        }
        this.custom.querySelector('[data-cpr-custom-total]').textContent = this.money(cents);
        this.custom.querySelector('[data-cpr-custom-area]').textContent = metric
          ? `${Math.round((width * length) / 1000) / 10} m²`
          : `${Math.round(area * 10) / 10} sq ${unit}`;
        this.updatePrice(this.variant, cents);
        this.setButton(this.variant.available);
      }

      usesCustomEndpoint() {
        return !!(this.customEndpoint && this.variant?.virtual);
      }

      // Asks the custom size server for a variant of this rug and size (once per size). Returns its id, or null.
      customSizeVariant() {
        if (!this.usesCustomEndpoint() || !this.isCustom() || !this.customSize) return Promise.resolve(null);
        const { width, length, unit } = this.customSize;
        const key = `${width}x${length}${unit}`;
        if (this.prepared?.key === key) return this.prepared.promise;
        const promise = fetch(`${this.customEndpoint}/variant`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_id: this.product.id, width, length, unit }),
          signal: AbortSignal.timeout ? AbortSignal.timeout(10000) : undefined,
        })
          .then((response) => (response.ok ? response.json() : null))
          .then((result) => result?.variant_id || null)
          .catch(() => null)
          .then((id) => {
            // Let a later attempt (for example the click) try again after a failure.
            if (!id && this.prepared?.key === key) this.prepared = null;
            return id;
          });
        this.prepared = { key, promise };
        return promise;
      }

      setQuantity(quantity) {
        const input = this.form?.querySelector('[data-cpr-quantity]');
        if (input) input.value = quantity;
      }

      /* ---------- Add to cart (uses the theme's cart drawer / notification) ---------- */
      bindForm() {
        if (!this.form) return;
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
      }

      async onSubmit(event) {
        event.preventDefault();
        const button = this.form.querySelector('[data-cpr-atc]');
        const errorEl = this.form.querySelector('[data-cpr-error]');
        if (!button || button.disabled || button.classList.contains('is-adding')) return;
        errorEl.textContent = '';
        // Spinner on the button (and the sticky bar's) while the size is prepared and added.
        const stickyButton = this.sticky?.querySelector('[data-cpr-sticky-submit]');
        button.classList.add('is-adding');
        button.setAttribute('aria-busy', 'true');
        stickyButton?.classList.add('is-adding');
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        let customVariantId = null;
        const formData = (variantId) => {
          const data = new FormData(this.form);
          if (variantId) {
            data.set('id', variantId);
            data.set('quantity', '1');
          }
          if (cart && typeof cart.getSectionsToRender === 'function') {
            data.append(
              'sections',
              cart.getSectionsToRender().map((s) => s.id),
            );
            data.append('sections_url', window.location.pathname);
          }
          return data;
        };
        cart?.setActiveElement?.(document.activeElement);
        try {
          customVariantId = await this.customSizeVariant();
          const addToCart = (body) =>
            fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
              method: 'POST',
              headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
              body,
            });
          let response = await addToCart(formData(customVariantId));
          // A new variant takes several seconds (often 10–15) to reach the storefront: keep trying every second
          // for up to 20 seconds, then fall back to the unit-priced quantity.
          for (let attempt = 0; customVariantId && response.status === 422 && attempt < 20; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            response = await addToCart(formData(customVariantId));
          }
          if (customVariantId && !response.ok) response = await addToCart(formData(null));
          const result = await response.json();
          if (!response.ok || result.status) {
            errorEl.textContent = result.description || result.message || 'Could not add to cart';
            return;
          }
          if (cart && typeof cart.renderContents === 'function') {
            cart.classList.remove('is-empty');
            cart.renderContents(result);
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
          window.publish?.(window.PUB_SUB_EVENTS?.cartUpdate, {
            source: 'custom-product',
            productVariantId: result.variant_id,
          });
        } catch (error) {
          errorEl.textContent = 'Could not add to cart';
        } finally {
          button.classList.remove('is-adding');
          button.removeAttribute('aria-busy');
          stickyButton?.classList.remove('is-adding');
        }
      }

      /* ---------- Size guide ---------- */
      bindGuide() {
        this.guide = this.querySelector('[data-cpr-guide]');
        if (!this.guide) return;
        this.querySelectorAll('[data-cpr-guide-open]').forEach((b) =>
          b.addEventListener('click', () => this.openGuide(b)),
        );
        this.guide
          .querySelectorAll('[data-cpr-guide-close]')
          .forEach((b) => b.addEventListener('click', () => this.closeGuide()));
        this.guide.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeGuide();
        });
        this.guide.querySelectorAll('[data-cpr-guide-value]').forEach((option) =>
          option.addEventListener('click', () => {
            const input = this.optionInputs.find(
              (i) =>
                parseInt(i.dataset.cprOptionInput, 10) === this.sizePosition &&
                i.value === option.dataset.cprGuideValue,
            );
            if (input) {
              input.checked = true;
              this.onVariantChange();
            }
            this.closeGuide();
          }),
        );
      }

      openGuide(opener) {
        this.guideOpener = opener;
        this.guide.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        this.syncGuide();
        this.guide.querySelector('[role="dialog"]').focus();
      }

      closeGuide() {
        if (!this.guide || this.guide.hidden) return;
        this.guide.hidden = true;
        document.documentElement.style.overflow = '';
        this.guideOpener?.focus();
      }

      syncGuide() {
        if (!this.guide || !this.variant) return;
        const value = this.variant.options[this.sizePosition - 1];
        this.guide
          .querySelectorAll('[data-cpr-guide-value]')
          .forEach((o) => o.setAttribute('aria-selected', o.dataset.cprGuideValue === value ? 'true' : 'false'));
      }

      /* ---------- Accordion ---------- */
      bindAccordions() {
        this.querySelectorAll('[data-cpr-acc-toggle]').forEach((toggle) =>
          toggle.addEventListener('click', () => {
            const open = toggle.getAttribute('aria-expanded') !== 'true';
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            document.getElementById(toggle.getAttribute('aria-controls'))?.classList.toggle('is-open', open);
          }),
        );
      }

      /* ---------- Shortlist (kept in this browser) ---------- */
      bindSave() {
        const button = this.querySelector('[data-cpr-save]');
        if (!button) return;
        const key = 'cpr-shortlist';
        const read = () => {
          try {
            return JSON.parse(localStorage.getItem(key) || '[]');
          } catch (error) {
            return [];
          }
        };
        const render = (saved) => {
          button.setAttribute('aria-pressed', saved ? 'true' : 'false');
          button.querySelector('[data-cpr-save-label]').textContent = saved
            ? button.dataset.savedLabel || button.dataset.label
            : button.dataset.label;
        };
        render(read().includes(button.dataset.handle));
        button.addEventListener('click', () => {
          const list = read();
          const saved = list.includes(button.dataset.handle);
          const next = saved ? list.filter((h) => h !== button.dataset.handle) : [...list, button.dataset.handle];
          try {
            localStorage.setItem(key, JSON.stringify(next));
          } catch (error) {
            /* storage unavailable */
          }
          render(!saved);
        });
      }

      /* ---------- Delivery date ---------- */
      renderDelivery() {
        const delivery = this.querySelector('[data-cpr-delivery]');
        const target = delivery?.querySelector('[data-cpr-delivery-date]');
        const days = parseInt(delivery?.dataset.days || '0', 10);
        if (!target || !days) return;
        const date = new Date();
        date.setDate(date.getDate() + days);
        const locale = document.documentElement.lang || undefined;
        const text = date.toLocaleDateString(locale, { month: 'long', day: 'numeric' });
        target.textContent = `${delivery.dataset.prefix} ${text}.`;
      }

      /* ---------- Sticky add to cart ---------- */
      bindSticky() {
        this.sticky = this.querySelector('[data-cpr-sticky]');
        const button = this.form?.querySelector('[data-cpr-atc]');
        if (!this.sticky || !button || !('IntersectionObserver' in window)) return;
        this.sticky
          .querySelector('[data-cpr-sticky-submit]')
          ?.addEventListener('click', () => this.form.requestSubmit());
        const observer = new IntersectionObserver(([entry]) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.sticky.classList.toggle('is-visible', past);
          this.sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
          this.sticky.querySelector('[data-cpr-sticky-submit]').tabIndex = past ? 0 : -1;
        });
        observer.observe(button);
        this.syncSticky();
      }

      syncSticky() {
        if (!this.sticky || !this.variant) return;
        const detail = this.sticky.querySelector('[data-cpr-sticky-detail]');
        const checked = this.querySelector(`[data-cpr-option-input="${this.sizePosition}"]:checked`);
        const pickerLabel = checked?.closest('.cpr-value')?.querySelector('.cpr-value__label span')?.textContent.trim();
        if (detail)
          detail.textContent = this.sizePosition
            ? pickerLabel || formatSize(this.variant.options[this.sizePosition - 1])
            : this.product.variants.length === 1 && this.variant.title === 'Default Title'
              ? ''
              : this.variant.title;
      }
    },
  );
})();
